/**
 * AlliancePartnersPage — staff-side admin surface for managing partner
 * organizations (Task #69, Jun 9 2026). Frontend for the IPC handlers
 * shipped in Task #66b.
 *
 * Layout: master-detail.
 *   Top:    filter strip (tier dropdown + status dropdown + create button)
 *   Master: paginated org list with tier badge, status, commission
 *           summary, last-activity timestamp
 *   Detail: when an org is selected, slides in below with tabbed sub-
 *           views — Overview / Tier History / Accruals / Audit
 *
 * Permission gates:
 *   alliance_partner:read   — view everything on this page
 *   alliance_partner:write  — N/A here (attribution is set from CRM contact view)
 *   alliance_partner:admin  — create org, set tier, approve/reject/clawback accruals
 *
 * Source of truth for the program: docs/roles/alliance_partner_program_2026-06-08.md
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import CommissionRatePanel from "./partners/CommissionRatePanel";
import {
  Handshake, Award, AlertCircle, CheckCircle2, Clock, Loader2,
  RefreshCcw, ChevronRight, Plus, X, Send, FileSignature,
  DollarSign, TrendingUp, Users, History, Filter,
  Inbox, UserPlus, ShieldAlert,
} from "lucide-react";

import PortalSignInReset from "./partners/PortalSignInReset";
import AuthenticatorReset from "./partners/AuthenticatorReset";
import EditMobile from './partners/EditMobile';
import MobileField from './partners/MobileField';
import PartnerDocuments from './partners/PartnerDocuments';
import { readParam, writeParams } from '../lib/urlState';

/*
 * ⛔⛔ CONSUMED ONCE PER PAGE LOAD — Card PARTNER-LIST-REFRESH.
 *
 * `?org=` answers "which record was this browser on when it loaded", and that
 * question has exactly one answer per load. Without the latch, every REMOUNT
 * of this page re-opens whatever the URL last held: leave Alliance Partners,
 * come back, and a record you had closed springs open again — and worse, the
 * user's first click on that row CLOSES it, because the row toggles. A control
 * that does the opposite of what it looks like it will do is the exact class
 * of defect this card is fixing, so it must not be introduced by the fix.
 *
 * ⚠ MEASURED: adding the restore without this latch broke seven existing react
 * suites, all of them in the same way — the page auto-opened the org from a URL
 * a previous test had written, so the suite's own click closed it and the
 * member list never painted. The tests were right and the code was wrong.
 *
 * Module scope, not a ref: the latch must outlive the component, because the
 * component is what remounts. A real page load resets it, which is the only
 * moment the URL's answer is fresh. The mirror effect then rewrites `?org=` to
 * match whatever is actually open, so a remount self-corrects a stale value
 * rather than leaving it to mislead the next load.
 */
let _urlOrgConsumed = false;

/** @returns {string} the org id from `?org=`, once per page load, or ''. */
function consumeUrlOrg() {
  if (_urlOrgConsumed) return '';
  _urlOrgConsumed = true;
  return readParam('org');
}

/** Test seam: let a test exercise the first-load path more than once. */
export function __resetUrlOrgConsumedForTests() { _urlOrgConsumed = false; }
import OneTimeCredentialPanel from './partners/OneTimeCredentialPanel';
const TIER_COLOR = {
  silver: "#9CA3AF",
  gold: "#C9A227",
  platinum: "#6366F1",
  elite: "#7C3AED",
};
const TIER_LABEL = {
  silver: "Silver", gold: "Gold", platinum: "Platinum", elite: "Elite",
};
const STATUS_COLOR = {
  active: "#62BB46", paused: "#F26522", terminated: "#DC3545",
};
const ACCRUAL_STATUS_COLOR = {
  accrued: "#F26522", approved: "#2E8B98", paid_out: "#62BB46",
  clawed_back: "#DC3545", rejected: "#6C757D",
};

export default function AlliancePartnersPage({ user, onNavigate }) {
  const perms = useMemo(() => new Set(user?.permissions || []), [user]);
  const canRead  = perms.has("alliance_partner:read");
  const canAdmin = perms.has("alliance_partner:admin");
  // ⛔⛔ SEPARATE FROM canAdmin ON PURPOSE — Card PARTNER-MANUAL-COMMISSION.
  // `alliance_partner:admin` is held by `business` as well as admin/sysadmin, and
  // the commission percentage is the number a partner's money is computed from.
  // Daniel's rule for this card is admin/sysadmin only, so it has its own
  // RESTRICTED permission. This only decides whether the control is OFFERED;
  // the server handler is the gate and re-checks it.
  const canSetCommissionRate = perms.has("alliance_partner:commission:set-rate");
  // Credential-lifecycle ruling (Daniel, 2026-08-28). Separate from :admin on
  // purpose — issuance is a sales motion, reset is recovery.
  // This only decides whether the control is OFFERED; the server is the gate.
  const canIssueCredential = perms.has("alliance_partner:credential:issue");
  // Card PARTNER-RESET-STAFF. The PORTAL (Cognito) reset — the one that affects
  // partners.zignagenix.net, and as of 3 Oct 2026 the ONLY partner credential
  // control with a button: the legacy password reset and "Reset 2FA" are both
  // retired and refuse. :credential:reset resolves to {sysadmin, admin}
  // (Daniel, 3 Oct 2026). The server is the gate; this only offers the control.
  const canResetPortalSignIn = perms.has("alliance_partner:credential:reset");
  // Card C1. Promotion CREATES a contact, so it needs crm:create as well as
  // alliance_partner:write. The server checks both; this only decides whether to
  // offer the button, so a user who holds one but not the other is not invited
  // to press something that will be refused.
  const canWrite   = perms.has("alliance_partner:write");
  const canPromote = canWrite && perms.has("crm:create");

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTier, setFilterTier] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [pendingOrgId, setPendingOrgId] = useState(null);   // #72: report deep-link, opened once orgs load
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(null);

  /**
   * ⛔⛔ useCallback IS LOad-BEARING HERE, NOT TIDINESS — Card
   * PORTAL-RESET-READOUT-c, 6 Oct 2026.
   *
   * These were plain arrow functions, so every render of this page minted NEW
   * identities. OrgDetail's `load` is `useCallback(..., [orgId, flashErr])` and
   * its effect is `useEffect(() => { load(); }, [load])` — so a new `flashErr`
   * meant a new `load`, which REFIRED THE EFFECT AND REFETCHED THE ORG.
   *
   * ⇒ The chain that cost a partner their credential: a successful reset calls
   * flashOk → setOk(message) → this page re-renders → new flashErr → new load →
   * REFETCH at +0s. Three seconds later the timer fires setOk(null) → another
   * re-render → another new flashErr → REFETCH at +3s. `load()` sets
   * loading=true, OrgDetail swapped to its loading branch, the member rows
   * UNMOUNTED, and PortalSignInReset's one-time password went with them.
   *
   * MEASURED IN DANIEL'S BROWSER (6 Oct, 23:30:50Z): the server returned 200
   * carrying temporaryPassword; orgs:get refetched at +0s and +3s; the detail
   * subtree unmounted at +3s and remounted 160 ms later. No console error. He
   * saw the password vanish, assumed it had not worked, and pressed reset again
   * — CloudTrail shows AdminSetUserPassword OK at 11:13:44 AND 11:16:47 EDT.
   *
   * ⚠ The setTimeout is left alone. The banner SHOULD clear itself; what must
   * not happen is a banner timer reaching into a child and refetching.
   */
  /**
   * ⛔⛔ THE ONE-TIME PASSWORD LIVES HERE, ABOVE EVERYTHING THAT REMOUNTS —
   * Card PORTAL-RESET-READOUT-d. It used to live in the member row, and the
   * member row is destroyed by any org-detail refetch. See
   * partners/OneTimeCredentialPanel.js for the measured timeline.
   *
   * ⚠ `showCredential` is a useCallback for the same reason flashOk is: an
   * unstable identity travelling down into OrgDetail is precisely what caused
   * the refetch loop in the first place.
   */
  const [oneTime, setOneTime] = useState(null);
  const showCredential = useCallback((c) => setOneTime(c), []);

  /*
   * ⛔⛔ REFRESH REFRESHED THE LIST AND NOT THE RECORD — Card
   * PARTNER-LIST-REFRESH (Daniel's demo, 8 Oct 2026).
   *
   * The Refresh button called loadOrgs() and nothing else. The member list a
   * staff member is actually looking at lives in <OrgDetail>, which fetches
   * orgs:get in its own effect keyed on [orgId, flashErr] — neither of which
   * loadOrgs changes. So pressing Refresh while a partner record was open
   * re-read the twelve rows BEHIND the record and left the record itself
   * untouched. The referral queue above it had exactly the same gap.
   *
   * ⇒ Reported as "the Refresh button did not make the new user appear",
   * which is precisely what it did: it refreshed everything except the thing
   * on screen.
   *
   * A monotonic nonce is the fix rather than lifting the fetches up, because
   * the fetch has to stay inside OrgDetail: the detail mounts per row, and the
   * org-detail refetch is the thing PORTAL-RESET-READOUT-d proved must not be
   * reachable from a parent timer (it unmounted a subtree mid-readout and
   * destroyed a one-time password). A number going DOWN cannot do that — it
   * only ever re-runs an effect the child already owns.
   */
  const [refreshNonce, setRefreshNonce] = useState(0);

  const flashOk = useCallback((m) => { setOk(m); setTimeout(() => setOk(null), 3000); }, []);
  const flashErr = useCallback((m) => { setError(m); setTimeout(() => setError(null), 6000); }, []);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.invoke("alliance_partner:orgs:list", {
        tier: filterTier || undefined,
        status: filterStatus || undefined,
      });
      if (r && r.error) throw new Error(r.message || r.code);
      setOrgs(Array.isArray(r) ? r : []);
    } catch (e) {
      flashErr(e.message);
    } finally { setLoading(false); }
  }, [filterTier, filterStatus]);

  useEffect(() => { if (canRead) loadOrgs(); }, [canRead, loadOrgs]);

  /** One Refresh for everything on screen: the list, the open record, the queue. */
  const refreshAll = useCallback(() => {
    loadOrgs();
    setRefreshNonce((n) => n + 1);
  }, [loadOrgs]);

  // #72 slice 1: deep-link open from a report ("Partner" name click). Mirrors
  // the CRM cowork.crm.openContact hand-off. Two steps so the open survives the
  // orgs-load race and never depends on numeric coercion:
  //   1) consume the one-shot sessionStorage key ONCE on mount into pending state
  //      (removing the key immediately so a later AP visit can't re-trigger);
  //   2) open the org once the list has loaded and contains it — matched
  //      type-tolerantly (org.id is a BIGSERIAL bigint → pg returns it as a
  //      STRING, so Number()/=== against the string missed). We setSelectedOrgId
  //      with the list's own id value, exactly like a manual row click, so it
  //      flows through the same audited detail-view path. Retries on each orgs
  //      change until the row is present; pending is cleared only on success.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('cowork.ap.openOrg');
      if (raw) { sessionStorage.removeItem('cowork.ap.openOrg'); setPendingOrgId(raw); }
      else {
        /*
         * ⛔ `?org=` — Card PARTNER-LIST-REFRESH. Reuses the EXISTING pending
         * mechanism rather than adding a second way to open a record: the
         * race it solves (the id arrives before the list does) is identical,
         * and #72's comment above already explains why the match must be
         * string-tolerant — org.id is a BIGSERIAL and pg hands it back as a
         * string, so a Number() comparison silently missed.
         *
         * ⚠ The sessionStorage hand-off WINS when both are present. It is a
         * deliberate click somebody just made in another screen; the URL is
         * only ever a record of where this browser already was.
         */
        const fromUrl = consumeUrlOrg();
        if (fromUrl) setPendingOrgId(fromUrl);
      }
    } catch { /* sessionStorage may be unavailable */ }
  }, []);

  // Mirror the open org so a reload comes back to the same record. Id only —
  // never the partner's name, contact email or phone (src/lib/urlState.js has
  // no key that would accept one).
  useEffect(() => {
    writeParams({ org: selectedOrgId == null ? '' : String(selectedOrgId) });
  }, [selectedOrgId]);

  useEffect(() => {
    if (pendingOrgId == null || !canRead || orgs.length === 0) return;
    const match = orgs.find((o) => String(o.id) === String(pendingOrgId));
    if (match) { setSelectedOrgId(match.id); setPendingOrgId(null); }
    // else: orgs may still be loading — keep pendingOrgId and retry next change.
  }, [pendingOrgId, canRead, orgs]);

  // KPIs derived from the loaded org list
  const kpis = useMemo(() => {
    const counts = { silver: 0, gold: 0, platinum: 0, elite: 0 };
    let active = 0, paused = 0, terminated = 0;
    for (const o of orgs) {
      if (counts[o.current_tier] != null) counts[o.current_tier]++;
      if (o.status === "active") active++;
      else if (o.status === "paused") paused++;
      else if (o.status === "terminated") terminated++;
    }
    return { ...counts, active, paused, terminated, total: orgs.length };
  }, [orgs]);

  if (!canRead) {
    return (
      <div style={{ padding: 32, color: "#6C757D" }}>
        You do not have <code>alliance_partner:read</code> permission. Contact your
        Zignagenix business owner if you should be able to see this page.
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <Handshake size={28} color="#2E8B98" />
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "#212529" }}>Alliance Partners</h1>
          <div style={{ fontSize: 12, color: "#6C757D" }}>
            Strategic Alliance Partner Program — partner orgs, tier history, and commission accruals.{" "}
            <em>The Physician Diagnoses. The PEM Coordinates. Partners Refer.</em>
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={refreshAll} disabled={loading} style={btnGhost()}
            title="Reload the partner list, the referral queue, and the record open below">
            <RefreshCcw size={12} /> Refresh
          </button>
          {canAdmin && (
            <button onClick={() => setShowCreateModal(true)} style={btnPrimary()}>
              <Plus size={14} /> New partner org
            </button>
          )}
        </div>
      </div>

      {/* Toast */}
      {(error || ok) && (
        /*
         * ⚠⚠ THE TONE IS IN AN ATTRIBUTE, NOT ONLY IN THE COLOUR — Card P127.
         * This banner distinguished failure from success by background alone,
         * so "it did not report success for the half that failed" was a claim
         * no test could read: both states render a <div> with text in it. A
         * red arm that cannot see the difference between #FEE2E2 and #DCFCE7
         * passes whichever branch runs.
         */
        <div data-testid="page-flash" data-tone={error ? "error" : "ok"} style={{
          padding: "10px 14px", borderRadius: 8, marginBottom: 12,
          background: error ? "#FEE2E2" : "#DCFCE7",
          color: error ? "#991B1B" : "#166534",
          border: `1px solid ${error ? "#FCA5A5" : "#86EFAC"}`,
          fontSize: 13,
        }}>
          {error ? <><AlertCircle size={14} style={{ verticalAlign: "middle" }} /> {error}</>
                 : <><CheckCircle2 size={14} style={{ verticalAlign: "middle" }} /> {ok}</>}
        </div>
      )}

      {/*
        ⛔ RENDERED BY THE PAGE, so a refetch of the org detail below cannot
        reach it. It is cleared only by the staff member pressing the button.
      */}
      <OneTimeCredentialPanel credential={oneTime} onDone={() => setOneTime(null)} />

      {/* KPI strip */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: 10, marginBottom: 18,
      }}>
        <Kpi label="Silver"   value={kpis.silver}   color={TIER_COLOR.silver}   icon={<Award size={16} />} />
        <Kpi label="Gold"     value={kpis.gold}     color={TIER_COLOR.gold}     icon={<Award size={16} />} />
        <Kpi label="Platinum" value={kpis.platinum} color={TIER_COLOR.platinum} icon={<Award size={16} />} />
        <Kpi label="Elite"    value={kpis.elite}    color={TIER_COLOR.elite}    icon={<Award size={16} />} />
        <Kpi label="Active"   value={kpis.active}   color="#62BB46"            icon={<CheckCircle2 size={16} />} />
      </div>

      {/* Card C1 — the staff referral queue. Page-level, not inside OrgDetail:
          the queue is cross-org by nature (oldest referral first, whoever sent
          it), so it does not belong in a per-org tab strip. */}
      {canRead && (
        <ReferralQueue
          refreshNonce={refreshNonce}
          canWrite={canWrite}
          canPromote={canPromote}
          flashOk={flashOk}
          flashErr={flashErr}
        />
      )}

      {/* Filter strip */}
      <div style={{
        background: "white", borderRadius: 10, padding: 12, marginBottom: 12,
        border: "1px solid #E9ECEF", display: "flex", gap: 12, alignItems: "center",
      }}>
        <Filter size={14} color="#6C757D" />
        <span style={{ fontSize: 11, color: "#6C757D", fontWeight: 600 }}>FILTER</span>
        <select value={filterTier} onChange={e => setFilterTier(e.target.value)} style={fieldSelect()}>
          <option value="">All tiers</option>
          <option value="silver">Silver</option>
          <option value="gold">Gold</option>
          <option value="platinum">Platinum</option>
          <option value="elite">Elite</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={fieldSelect()}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="terminated">Terminated</option>
        </select>
        {(filterTier || filterStatus) && (
          <button onClick={() => { setFilterTier(""); setFilterStatus(""); }} style={btnGhost()}>
            <X size={11} /> Clear filters
          </button>
        )}
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#6C757D" }}>
          {orgs.length} org{orgs.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Org list */}
      <div style={{
        background: "white", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.06)",
        border: "1px solid #E9ECEF", overflow: "hidden",
      }}>
        {/*
          ⛔⛔ A REFRESH MUST NOT UNMOUNT THE RECORD THE USER IS READING.
          Card PARTNER-LIST-REFRESH, and this is a MEASURED hazard, not a
          theoretical one.

          This used to read `{loading && <spinner/>}` + `{!loading && rows}`.
          Because loadOrgs sets loading = true for the duration of the fetch,
          pressing Refresh REPLACED the whole row list — and therefore the open
          <OrgDetail> — with a spinner, then built it again from scratch.

          That unmount/remount is the exact event
          partners/OneTimeCredentialPanel.js documents from Daniel's browser on
          6 Oct: "the detail subtree unmounted at +3s and remounted 160 ms
          later", destroying a one-time temporary password mid-readout. The
          staff member assumed the reset had failed and ran it again.

          So the row list now stays mounted whenever there is anything to show,
          and the spinner is for the FIRST load only. A refresh of an open
          record is now what it says it is — a refetch, by the nonce the
          children watch — rather than a teardown that happens to re-fetch on
          the way back up.

          ⚠ The nonce is what makes the data actually update now. Before this
          change the remount did it by accident, which is why removing the
          nonce used to leave the suite green: the bug and the fix were both
          invisible to a test. They are not any more.
        */}
        {loading && orgs.length === 0 && (
          <div style={{ padding: 28, textAlign: "center", color: "#6C757D" }}>
            <Loader2 size={18} className="spin" style={{ verticalAlign: "middle" }} />
            <span style={{ marginLeft: 6 }}>Loading partner orgs…</span>
          </div>
        )}
        {!loading && orgs.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", color: "#6C757D", fontSize: 13 }}>
            No partner organizations yet.
            {canAdmin && (
              <div style={{ fontSize: 11, color: "#ADB5BD", marginTop: 6 }}>
                Click "New partner org" above to create your first one.
              </div>
            )}
          </div>
        )}
        {orgs.length > 0 && (
          <>
            <div style={{
              display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1.6fr 1fr 40px",
              padding: "8px 16px", background: "#F8F9FA", fontSize: 11,
              color: "#6C757D", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
            }}>
              <div>Organization</div>
              <div>Tier</div>
              <div>Status</div>
              <div>Owner</div>
              <div>Created</div>
              <div></div>
            </div>
            {orgs.map(org => (
              <React.Fragment key={org.id}>
                <div
                  onClick={() => setSelectedOrgId(selectedOrgId === org.id ? null : org.id)}
                  style={{
                    display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1.6fr 1fr 40px",
                    padding: "12px 16px", borderBottom: "1px solid #F1F3F5",
                    fontSize: 13, alignItems: "center", cursor: "pointer",
                    background: selectedOrgId === org.id ? "#F0F4F8" : "white",
                    transition: "background .1s",
                  }}
                  onMouseEnter={e => { if (selectedOrgId !== org.id) e.currentTarget.style.background = "#F8F9FA"; }}
                  onMouseLeave={e => { if (selectedOrgId !== org.id) e.currentTarget.style.background = "white"; }}
                >
                  <div>
                    <strong>{org.name}</strong>
                    {org.legal_name && org.legal_name !== org.name && (
                      <div style={{ fontSize: 10, color: "#ADB5BD" }}>{org.legal_name}</div>
                    )}
                  </div>
                  <div>
                    <span style={{
                      background: TIER_COLOR[org.current_tier] || "#9CA3AF",
                      color: "white", padding: "2px 8px", borderRadius: 999,
                      fontSize: 10, fontWeight: 700,
                    }}>{TIER_LABEL[org.current_tier] || org.current_tier}</span>
                  </div>
                  <div>
                    <span style={{
                      background: STATUS_COLOR[org.status] || "#6C757D",
                      color: "white", padding: "2px 8px", borderRadius: 999,
                      fontSize: 10, fontWeight: 700,
                    }}>{org.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#6C757D" }}>
                    {org.contact_email || "—"}
                  </div>
                  <div style={{ fontSize: 11, color: "#6C757D" }}>
                    {org.created_at ? new Date(org.created_at).toLocaleDateString() : "—"}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <ChevronRight size={14} color="#ADB5BD"
                      style={{ transform: selectedOrgId === org.id ? "rotate(90deg)" : "none", transition: "transform .15s" }} />
                  </div>
                </div>
                {selectedOrgId === org.id && (
                  <OrgDetail
                    orgId={org.id}
                    refreshNonce={refreshNonce}
                    canAdmin={canAdmin}
                    canSetCommissionRate={canSetCommissionRate}
                    canIssueCredential={canIssueCredential}
                    canResetPortalSignIn={canResetPortalSignIn}
                    onClose={() => setSelectedOrgId(null)}
                    onChanged={loadOrgs}
                    flashOk={flashOk}
                    flashErr={flashErr}
                    showCredential={showCredential}
                  />
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </div>

      {/* Create Org modal */}
      {showCreateModal && (
        <CreateOrgModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); loadOrgs(); flashOk("Partner org created."); }}
          flashErr={flashErr}
        />
      )}
    </div>
  );
}

// ─── Detail panel (slides in below the selected row) ────────────────────────
/**
 * Card C1 — the staff referral queue.
 *
 * Lists quarantined partner submissions, oldest first, and lets a named human
 * promote one into a CRM contact or reject it. Nothing here is automatic and
 * nothing goes back to the partner.
 *
 * ⚠⚠ PROMOTION IS IRREVERSIBLE. crm_contacts.alliance_partner_id is write-once
 * (a migration-144 trigger rejects any later change), so a referral credited to
 * the wrong partner CANNOT be corrected by editing it afterwards. That is why
 * promoting is a two-step confirm that names the partner org in full rather than
 * a single button, and why the server also requires the org id to be echoed
 * back and refuses a mismatch. The friction is the point.
 */
function ReferralQueue({ canWrite, canPromote, flashOk, flashErr, refreshNonce = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [drain, setDrain] = useState(null);
  // A refusal has to stay on screen, next to the control that caused it. The
  // page-top toast auto-clears after six seconds and sits well above the queue,
  // so during the 2026-08-27 demo a real duplicate-phone refusal was read as the
  // button doing nothing at all. This one persists until the operator moves on.
  const [promoteErr, setPromoteErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.invoke("alliance_partner:submissions:list", { limit: 50 });
      if (r?.error) { flashErr(r.message || "Could not load the referral queue."); setRows([]); }
      else { setRows(r?.submissions || []); setDrain(r?.drain || null); }
    } catch {
      flashErr("Could not load the referral queue.");
      setRows([]);
    } finally {
      setLoading(false);
    }
    // `refreshNonce` is a dependency, not a parameter: the page's Refresh
    // button increments it, and this queue reloads because of it. Before this,
    // Refresh reloaded the org list and left the queue showing whatever it
    // held when it mounted.
  }, [flashErr, refreshNonce]);

  useEffect(() => { load(); }, [load]);

  function openConfirm(row) {
    // Pre-split the name for convenience, but the human confirms it: both CRM
    // columns are NOT NULL and a space is not a reliable name boundary.
    const parts = String(row.prospectName || "").trim().split(/\s+/);
    setFirstName(parts[0] || "");
    setLastName(parts.length > 1 ? parts.slice(1).join(" ") : "");
    setConfirmId(row.id);
    setPromoteErr(null);
  }

  async function promote(row) {
    setBusyId(row.id);
    setPromoteErr(null);
    try {
      // ⛔⛔ THE CONFIRMATION MUST MATCH THE PATH THE HANDLER WILL TAKE.
      //
      // Rulings A and B (2026-09-25) gave promote three paths, each demanding a
      // DIFFERENT explicit confirmation, and the handler REFUSES a confirmation
      // that does not belong to the row's path. Sending confirmPartnerOrgId for
      // every row — which is what this did before — means a null-partner or
      // inactive-partner referral can never be promoted from this screen at all:
      // it is kept, flagged, shown as promotable, and then stuck.
      //
      // ⚠ `confirmationRequired` comes FROM THE SERVER, from the same predicate
      // the handler dispatches on. Re-deriving it here from attributionStatus
      // would be a second copy of the rule, free to drift.
      const payload = { quarantineId: row.id, firstName, lastName };
      if (row.confirmationRequired === "no_partner") payload.confirmNoPartner = true;
      else if (row.confirmationRequired === "no_credit") payload.confirmNoCredit = true;
      else payload.confirmPartnerOrgId = row.organizationId;

      const r = await window.zignagenixAPI.invoke("alliance_partner:submissions:promote", payload);
      // Both surfaces, deliberately: the toast for anyone watching the page, and
      // the inline panel for the operator whose eyes are on this row.
      if (r?.error) {
        const msg = r.message || "Could not promote this referral.";
        setPromoteErr(msg);
        flashErr(msg);
      } else {
        flashOk(`Referral promoted into contact ${r.contactId}.`);
        setConfirmId(null);
        await load();
      }
    } catch {
      const msg = "Could not promote this referral. Check your connection and try again.";
      setPromoteErr(msg);
      flashErr(msg);
    } finally {
      setBusyId(null);
    }
  }

  async function reject(row) {
    setBusyId(row.id);
    try {
      const r = await window.zignagenixAPI.invoke("alliance_partner:submissions:reject", {
        quarantineId: row.id,
      });
      if (r?.error) flashErr(r.message || "Could not reject this referral.");
      else { flashOk("Referral rejected."); await load(); }
    } catch {
      flashErr("Could not reject this referral.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{
      border: "1px solid #DEE2E6", borderRadius: 10, background: "#fff",
      marginBottom: 18, overflow: "hidden",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "12px 16px", borderBottom: "1px solid #E9ECEF", background: "#F8F9FA",
      }}>
        <Inbox size={18} color="#2E8B98" />
        <strong style={{ fontSize: 14, color: "#212529" }}>Referral queue</strong>
        <span style={{ fontSize: 12, color: "#6C757D" }}>
          {loading ? "loading…" : `${rows.length} waiting`}
          {drain?.moved ? ` · ${drain.moved} newly received` : ""}
          {drain?.failed ? " · transfer check failed" : ""}
        </span>
        <button onClick={load} disabled={loading} style={{ ...btnGhost(), marginLeft: "auto" }}>
          <RefreshCcw size={12} /> Refresh
        </button>
      </div>

      {loading && (
        <div style={{ padding: 20, textAlign: "center", color: "#6C757D", fontSize: 13 }}>
          <Loader2 size={16} className="spin" /> Checking for new referrals…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div style={{ padding: 20, textAlign: "center", color: "#6C757D", fontSize: 13 }}>
          Nothing waiting.
        </div>
      )}

      {!loading && rows.map((row) => {
        const confirming = confirmId === row.id;
        const busy = busyId === row.id;
        return (
          <div key={row.id} style={{ padding: "12px 16px", borderBottom: "1px solid #F1F3F5" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
              <div style={{ minWidth: 220, flex: 1 }}>
                {row.readable ? (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#212529" }}>{row.prospectName}</div>
                    <div style={{ fontSize: 12, color: "#495057" }}>
                      {row.prospectPhone}{row.prospectEmail ? ` · ${row.prospectEmail}` : ""}
                    </div>
                  </>
                ) : (
                  // Never render the decrypt sentinel where a name belongs.
                  <div style={{ fontSize: 13, color: "#991B1B", display: "flex", alignItems: "center", gap: 6 }}>
                    <ShieldAlert size={14} /> Details could not be read. This referral cannot be promoted.
                  </div>
                )}
                {/*
                  ⛔⛔ THE "ref …" LINE IS GONE — Card PARTNER-NO-REF, Daniel's
                  ruling of 3 Oct 2026: "No number at all."

                  It used to print the short code for a legacy row and the words
                  "not issued" for a portal one. Both halves had to go, and the
                  SECOND half is the one that mattered: no partner is shown a
                  number any more, so a code here could only ever be something
                  the person on the phone cannot quote, and "not issued" is a
                  status for a thing that no longer exists.

                  ⚠ WHY THIS DOES NOT LEAVE STAFF SHORT. Identification is
                  patient name + partner + time, and all three are on this row
                  already — the name above, the org and the timestamp below.
                  That is exactly what the portal's receipt now tells the
                  partner to give on the phone, so both ends match.

                  ⚠⚠ THE ONE CASE THAT GENUINELY LOSES SOMETHING, STATED RATHER
                  THAN GLOSSED: on a row whose details will not DECRYPT, the
                  reference used to be the only handle. It is not any more — but
                  nor was it useful, because a portal row never had one, and a
                  partner was never shown the CRM's code in the first place.
                  Such a row is still identified by partner + submitted-at,
                  which is how an operator would chase it regardless.

                  ⛔ submission_id is deliberately NOT rendered in its place. It
                  is the internal idempotency key; showing it would recreate the
                  exact failure this card removes — a code on screen that the
                  partner has never seen. No DB column is dropped; nothing here
                  is a migration.
                */}
                <div style={{ fontSize: 11, color: "#6C757D", marginTop: 4 }}>
                  from <strong>{row.organizationName || `org ${row.organizationId}`}</strong>
                  {row.partnerUserEmail ? ` (${row.partnerUserEmail})` : ""}
                  {row.submittedAt ? ` · ${new Date(row.submittedAt).toLocaleString()}` : ""}
                  {row.organizationStatus === "terminated" && (
                    <span style={{ color: "#991B1B" }}> · partner terminated</span>
                  )}
                </div>
              </div>

              {!confirming && (
                <div style={{ display: "flex", gap: 8 }}>
                  {canPromote && row.promotable && (
                    <button onClick={() => openConfirm(row)} disabled={busy} style={btnPrimary()}>
                      <UserPlus size={14} /> Promote
                    </button>
                  )}
                  {canWrite && (
                    <button onClick={() => reject(row)} disabled={busy} style={btnGhost()}>
                      <X size={12} /> Reject
                    </button>
                  )}
                </div>
              )}
            </div>

            {confirming && (
              <div style={{
                marginTop: 10, padding: 12, borderRadius: 8,
                background: "#FFFBEB", border: "1px solid #FDE68A",
              }}>
                {/*
                  ⚠⚠ THE WARNING MUST DESCRIBE THE CLAIM BEING MADE, and the three
                  paths make three DIFFERENT claims. Showing "this credits the
                  referral to X" on a row that credits nobody would ask a staff
                  member to confirm something untrue — and this is the one screen
                  where the mistake is unrecoverable, because
                  crm_contacts.alliance_partner_id is write-once.
                */}
                <div style={{ fontSize: 12, color: "#92400E", marginBottom: 10 }}>
                  <AlertCircle size={13} style={{ verticalAlign: "middle" }} />{" "}
                  {row.confirmationRequired === "no_partner" ? (
                    <>
                      <strong>No partner could be identified</strong> for this referral, so it
                      will be created with <strong>no partner attribution</strong> and no
                      commission. The prospect is still a real referral and is kept. If the
                      partner is identified later, attribution can still be set once — promoting
                      it now does not close that off.
                    </>
                  ) : row.confirmationRequired === "no_credit" ? (
                    <>
                      This referral came from <strong>{row.organizationName || `org ${row.organizationId}`}</strong>,
                      whose account is <strong>inactive</strong>. It will be created{" "}
                      <strong>without credit</strong> — no commission and no attribution — pending
                      a contract decision on inactive partners. The submitting organisation stays
                      recorded on the referral, so credit can be revisited.
                    </>
                  ) : (
                    <>
                      This credits the referral to <strong>{row.organizationName || `org ${row.organizationId}`}</strong>{" "}
                      permanently. Partner attribution cannot be changed afterwards, so check it now.
                    </>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  <label style={{ fontSize: 12, color: "#495057" }}>
                    First name
                    <input
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      style={{ display: "block", padding: "6px 8px", border: "1px solid #CED4DA", borderRadius: 6 }}
                    />
                  </label>
                  <label style={{ fontSize: 12, color: "#495057" }}>
                    Last name
                    <input
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      style={{ display: "block", padding: "6px 8px", border: "1px solid #CED4DA", borderRadius: 6 }}
                    />
                  </label>
                </div>
                {promoteErr && (
                  <div
                    role="alert"
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 8,
                      padding: "10px 12px", marginBottom: 10, borderRadius: 6,
                      background: "#FEF2F2", border: "1px solid #FCA5A5",
                      color: "#991B1B", fontSize: 13, lineHeight: 1.45,
                    }}
                  >
                    <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>{promoteErr}</span>
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => promote(row)}
                    disabled={busy || !firstName.trim() || !lastName.trim()}
                    style={busy || !firstName.trim() || !lastName.trim() ? btnDisabled() : btnPrimary()}
                  >
                    {busy ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />}{" "}
                    {row.confirmationRequired === "no_partner"
                      ? "Create contact with no attribution"
                      : row.confirmationRequired === "no_credit"
                        ? "Create contact without credit"
                        : "Confirm and create contact"}
                  </button>
                  <button onClick={() => setConfirmId(null)} disabled={busy} style={btnGhost()}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * ⛔⛔ canResetPortalSignIn IS A PROP, NOT A CLOSURE. It is computed in
 * AlliancePartnersPage from the session's permissions and MUST be threaded in
 * here explicitly.
 *
 * #581 computed it in the parent and used it in the members map inside this
 * component. OrgDetail is a separate function component, so the name was simply
 * out of scope and every org detail threw
 *     ReferenceError: canResetPortalSignIn is not defined
 * the moment a member row rendered — staff could not open ANY partner
 * organisation. It shipped green because the only test for the control rendered
 * PortalSignInReset in isolation; nothing rendered OrgDetail with members.
 *
 * ⚠ Every permission this component branches on arrives the same way —
 * canAdmin, canIssueCredential, and now this. If you add another, add it to
 * BOTH this list and the <OrgDetail …> call site.
 */
/**
 * Who deactivated this member, and when — Card P124.
 *
 * ⛔⛔ THREE STATES, NOT TWO (the #686 rule, applied to a sub-section).
 * `events` is an ARRAY when the audit read succeeded, NULL when it threw, and
 * EMPTY when this organisation has simply never had a member switched off.
 * Collapsing null into empty would print "no history" over a failed query —
 * which, on a screen whose whole job is to say who did what, is the one lie
 * that matters here.
 *
 * ⚠ Renders nothing at all for a member with no event: a line saying "never
 * deactivated" on every row of a healthy partner is noise.
 */
function MemberStatusTrail({ userId, events }) {
  if (events === null || events === undefined) {
    return (
      <span data-testid={`member-trail-unavailable-${userId}`}
        style={{ marginLeft: 6, fontSize: 10, color: "#92400E" }}>
        · status history unavailable
      </span>
    );
  }
  const ev = events.find((e) => String(e.partner_user_id) === String(userId));
  if (!ev) return null;
  const verb = ev.action === "PARTNER_MEMBER_REACTIVATED" ? "Reactivated" : "Deactivated";
  const when = (() => {
    try { return new Date(ev.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return ""; }
  })();
  return (
    <span data-testid={`member-trail-${userId}`} style={{ marginLeft: 6, fontSize: 10, color: "#868E96" }}>
      · {verb}{ev.actor_name ? ` by ${ev.actor_name}` : ""}{when ? ` on ${when}` : ""}
    </span>
  );
}

/**
 * Confirm deactivating or reactivating ONE member, and collect the reason the
 * server demands — Card P124.
 *
 * ⛔⛔ IT SAYS WHAT HAPPENS, IN THE ORDER IT HAPPENS. The P108 triage found
 * this product full of controls that act without saying what they do; this one
 * is destructive and partly IRREVERSIBLE, so the asymmetry is stated outright
 * rather than discovered afterwards:
 *
 *   · deactivating ends sessions AND disables the portal sign-in;
 *   · reactivating restores BOTH halves.
 *
 * ⭐⭐ THE ASYMMETRY IS GONE — Card P127, and the apology that stood here is
 * removed rather than softened. P124 had to say "re-enabling the portal
 * sign-in is not available from this page", because `AdminEnableUser` was not
 * in the task role's policy and P120 had deliberately shipped no `enableUser`
 * verb rather than one that fails with AccessDenied. Daniel granted the action
 * on 26 Oct; the verb and its caller now exist, so the sentence is FALSE and a
 * false warning is worse than none — it trains staff to escalate something
 * the page can do.
 *
 * ⛔⛔ BUT THE DIALOG STILL DOES NOT PROMISE THE PORTAL HALF. The grant is
 * applied by Daniel SEPARATELY from this merge, and the provider can fail for
 * its own reasons, so reactivation reports which halves actually landed from
 * the SERVER'S answer (`cognito.reEnabled`) instead of asserting both from the
 * mere fact that the call returned. A dialog that said "portal sign-in
 * restored" unconditionally would be the same confident-wrong readout in the
 * opposite direction.
 *
 * ⚠ The 5-character minimum MIRRORS routes/alliance_partners.js. It is not a
 * second rule: the server refuses a shorter reason with REASON_REQUIRED
 * whatever this form does, and a red arm proves the blank case cannot be
 * submitted here either.
 */
function MemberActionConfirm({ user, mode, onCancel, onDone, run }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const tooShort = reason.trim().length < 5;
  const deactivating = mode === "deactivate";

  async function submit(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (tooShort || busy) return;
    setBusy(true); setError("");
    try {
      // ⛔ The RESULT is handed up, not discarded. The server says which halves
      // landed (Card P127); a caller that ignored it could only ever flash an
      // unconditional "Reactivated".
      const r = await run(user, mode, reason.trim());
      onDone(r);
    } catch (err) {
      setError((err && err.message) || "Could not reach the server. Nothing was changed.");
      setBusy(false);
    }
  }

  return (
    <div data-testid="member-action-confirm" role="dialog" aria-modal="true"
      aria-label={deactivating ? "Deactivate member" : "Reactivate member"}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex",
               alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
      <form onSubmit={submit} style={{ background: "#fff", borderRadius: 10, padding: 20,
        maxWidth: 520, width: "100%", boxShadow: "0 10px 40px rgba(0,0,0,.25)" }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 15, color: "#212529" }}>
          {deactivating ? "Deactivate" : "Reactivate"} {user.email}
        </h3>

        <ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 12.5, color: "#495057", lineHeight: 1.6 }}>
          {deactivating ? (
            <>
              <li>Their current sessions end immediately.</li>
              <li>Their portal sign-in at partners.zignagenix.net is disabled.</li>
              <li>This can be undone from here — reactivating restores both.</li>
              <li>The reason below is recorded against the organisation.</li>
            </>
          ) : (
            <>
              <li>Their ZignaGenix access is restored.</li>
              <li>Their portal sign-in at partners.zignagenix.net is re-enabled.</li>
              <li>Existing sessions are not restored; they sign in afresh.</li>
              <li>The reason below is recorded against the organisation.</li>
            </>
          )}
        </ul>

        <label htmlFor="member-action-reason"
          style={{ display: "block", fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
          Reason (required)
        </label>
        <input id="member-action-reason" data-testid="member-action-reason"
          value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
          placeholder={deactivating ? "e.g. left the clinic on 24 Oct" : "e.g. rehired, termination was a billing dispute"}
          style={{ width: "100%", padding: "7px 9px", fontSize: 12.5, borderRadius: 6,
                   border: "1px solid #CED4DA", marginBottom: 4 }} />
        <div style={{ fontSize: 10.5, color: tooShort ? "#991B1B" : "#868E96", marginBottom: 12 }}>
          {tooShort ? "At least 5 characters — the server requires one too." : "Recorded in the partner audit log."}
        </div>

        {error && (
          <div data-testid="member-action-error" role="alert" style={{ background: "#FEE2E2",
            border: "1px solid #FCA5A5", color: "#991B1B", borderRadius: 6,
            padding: "8px 10px", fontSize: 12, marginBottom: 12 }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onCancel} disabled={busy}
            style={{ padding: "7px 14px", fontSize: 12, borderRadius: 6, border: "1px solid #CED4DA",
                     background: "#fff", cursor: "pointer" }}>Cancel</button>
          <button type="submit" data-testid="member-action-submit" disabled={tooShort || busy}
            style={{ padding: "7px 14px", fontSize: 12, fontWeight: 700, borderRadius: 6, border: "none",
                     background: tooShort || busy ? "#E9ECEF" : (deactivating ? "#DC3545" : "#15803D"),
                     color: tooShort || busy ? "#ADB5BD" : "#fff",
                     cursor: tooShort || busy ? "default" : "pointer" }}>
            {busy ? "Working…" : (deactivating ? "Deactivate member" : "Reactivate member")}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Say what terminating an organisation will do, with the REAL number, before
 * it is done — Card P124.
 *
 * ⛔⛔ THE COUNT IS COMPUTED FROM THE MEMBERS ON SCREEN, not guessed and not
 * hard-coded. It is the same set the server's cascade hits
 * (`WHERE organization_id = ? AND is_active = TRUE`), so the sentence and the
 * transaction cannot disagree — and a red arm changes the fixture's member
 * count and requires the sentence to follow.
 *
 * ⚠ "0 members" IS STILL WORTH SAYING. A terminated org with nobody in it is
 * the ordinary case for the demo partner, and a dialog that silently omits
 * the line when the count is zero teaches people the line means nothing.
 */
function TerminateOrgConfirm({ org, users, onCancel, onConfirm, busy }) {
  const activeCount = (users || []).filter((u) => u.is_active !== false).length;
  return (
    <div data-testid="terminate-org-confirm" role="dialog" aria-modal="true" aria-label="Terminate organisation"
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex",
               alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 10, padding: 20, maxWidth: 520, width: "100%",
                    boxShadow: "0 10px 40px rgba(0,0,0,.25)" }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 15, color: "#212529" }}>
          Terminate {org.name}?
        </h3>
        <ul style={{ margin: "0 0 14px", paddingLeft: 18, fontSize: 12.5, color: "#495057", lineHeight: 1.6 }}>
          <li data-testid="terminate-member-count">
            <strong>This will deactivate {activeCount} member{activeCount === 1 ? "" : "s"} and end their sessions.</strong>
          </li>
          <li>No new commissions accrue. Existing accruals remain and still pay out.</li>
          <li>New patients can no longer be attributed to this partner.</li>
          <li>Reactivating a member later restores both their ZignaGenix access and their portal sign-in.</li>
        </ul>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onCancel} disabled={busy}
            style={{ padding: "7px 14px", fontSize: 12, borderRadius: 6, border: "1px solid #CED4DA",
                     background: "#fff", cursor: "pointer" }}>Cancel</button>
          <button type="button" data-testid="terminate-confirm" onClick={onConfirm} disabled={busy}
            style={{ padding: "7px 14px", fontSize: 12, fontWeight: 700, borderRadius: 6, border: "none",
                     background: busy ? "#E9ECEF" : "#DC3545", color: busy ? "#ADB5BD" : "#fff",
                     cursor: busy ? "default" : "pointer" }}>
            {busy ? "Working…" : "Terminate organisation"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrgDetail({ orgId, refreshNonce = 0, canAdmin, canSetCommissionRate, canIssueCredential, canResetPortalSignIn, onClose, onChanged, flashOk, flashErr, showCredential }) {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [accruals, setAccruals] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Credential issuance. ⛔ The ONE-TIME password is NOT held here any more —
  // it goes to the PAGE via showCredential (Card PORTAL-RESET-READOUT-d),
  // because this subtree is unmounted by any org-detail refetch.
  const [issueEmail, setIssueEmail] = useState("");
  /**
   * ⚠ REQUIRED (Card PARTNER-PHONE-SMS): the partner's 2FA fallback.
   * ⛔ HOLDS E.164 OR THE EMPTY STRING, NEVER A HALF-TYPED NATIONAL NUMBER —
   * Card SALES-REP-EASE. <MobileField> owns the country and the typed digits
   * and only ever hands up a converted number, so every guard below (and the
   * submit button's own disabled rule) keeps working unchanged.
   */
  const [issueMobile, setIssueMobile] = useState("");
  /**
   * ⚠ Bumped after a successful issue so <MobileField> remounts back to its
   * default country and an empty field. Clearing `issueMobile` alone would
   * leave the rep's typed digits and chosen country sitting in the child's own
   * state, ready to ride along with the NEXT partner.
   */
  const [mobileFieldKey, setMobileFieldKey] = useState(0);
  // null = create a new member; an id = link to that existing row.
  const [linkUserId, setLinkUserId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, accr, aud] = await Promise.all([
        window.zignagenixAPI.invoke("alliance_partner:orgs:get", orgId),
        window.zignagenixAPI.invoke("alliance_partner:accruals:list", { orgId, limit: 100 }),
        window.zignagenixAPI.invoke("alliance_partner:audit:list", { orgId, limit: 50 }),
      ]);
      if (d && !d.error) setData(d);
      if (Array.isArray(accr)) setAccruals(accr);
      if (Array.isArray(aud)) setAudit(aud);
    } catch (e) {
      flashErr(e.message);
    } finally { setLoading(false); }
    // `refreshNonce` is a dependency, not a parameter — see the comment on the
    // nonce in the page component. This is the line that makes the page's
    // Refresh button reach the record the staff member is actually reading.
  }, [orgId, flashErr, refreshNonce]);
  useEffect(() => { load(); }, [load]);

  /*
   * ⛔⛔ THE CONTROL THE GATE ALWAYS IMPLIED, AND NOBODY EVER BUILT.
   *
   * routes/alliance_partners.js has had `orgs:record-agreement` since the BAA
   * gate landed, with a comment saying in as many words: "The admin path the
   * BAA gate implies: something has to be able to RECORD the paperwork, or the
   * hard refuse is a dead end." It was never called from anywhere in src/ —
   * `grep -rn baaSignedAt src/` returned exactly one line, a READ-ONLY <Field>
   * rendering the date.
   *
   * So the dead end was real and total: create a partner org (baa_signed_at is
   * NULL by construction — orgs:create inserts no date), press Issue login,
   * and be refused AGREEMENT_NOT_RECORDED with "see admin" — where "admin" had
   * no control either. That is the 8 Oct demo, exactly.
   *
   * ⚠ A DATE A HUMAN ASSERTS, not an upload. The server's own comment explains
   * why, and it is worth repeating at the call site: a file upload here would
   * arrive with no retention story attached.
   */
  async function recordAgreement(kind, value) {
    if (!canAdmin || busy) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("alliance_partner:orgs:record-agreement", {
        organizationId: orgId,
        ...(kind === "baa" ? { baaSignedAt: value } : { contractSignedAt: value }),
      });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk(kind === "baa"
        ? "Partner agreement recorded — a portal login can now be issued."
        : "Contract date recorded.");
      await load();
      onChanged();
    } catch (e) { flashErr(e.message); }
    finally { setBusy(false); }
  }

  /*
    ⛔⛔ setTier() IS GONE — Card PARTNER-MANUAL-COMMISSION.

    Not because the tier was deleted (it was not: current_tier, the tier history
    table, the Tier History tab and ledger tier_at_earn all stand), but because
    the tier no longer decides anybody's rate. A dropdown that still looked like
    it set a partner's commission, and silently did nothing to it, is a worse
    artefact than no dropdown — the next person to use it would believe they had
    changed what a partner is paid.

    ⚠ The SERVER handler alliance_partner:orgs:set-tier is deliberately LEFT IN
    PLACE and still writes tier history. Removing it was not asked for, auto
    promotion (scripts/alliance_partner_tier_recalc.js) still maintains the
    column, and deleting a working audited write path to tidy up a UI change is
    exactly the kind of scope creep that breaks something nobody was looking at.
  */

  /*
    ⛔⛔ THE RATE, Card PARTNER-MANUAL-COMMISSION. Note what is NOT here: no
    optimistic update of org.commission_rate_pct. The server decides — it
    re-validates, it STAMPS the effective date itself (review round 1, P65a:
    a client-supplied effectiveFrom is refused outright, because the first cut
    stored one and then ignored it), and it is the only thing that knows whether
    the write actually committed — so the panel shows the stored value and
    nothing else, and a reload is what makes it current.
  */
  async function setCommissionRate({ ratePct, reason }) {
    if (!canSetCommissionRate || busy) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("alliance_partner:orgs:set-commission-rate", {
        id: orgId, ratePct, reason,
      });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk(
        r && r.noop
          ? "Commission rate unchanged."
          : `Commission rate set to ${r.to == null ? "not set" : r.to + "%"}.`
      );
      await load();
      onChanged();
    } catch (e) { flashErr(e.message); }
    finally { setBusy(false); }
  }

  /*
   * ⛔⛔ MEMBER DEACTIVATION HAS A BUTTON NOW — Card P124.
   *
   * #702 shipped alliance_partner:members:deactivate / :reactivate as IPC
   * channels and stopped there: terminating a whole ORG cascades
   * automatically, but switching off ONE person — the ordinary case, someone
   * leaving a partner clinic — still needed a developer to craft a call. A
   * capability that exists only for people who can write IPC is not a
   * capability the business has.
   */
  const [memberAction, setMemberAction] = useState(null);   // { user, mode }
  const [pendingTermination, setPendingTermination] = useState(false);

  async function runMemberAction(user, mode, reason) {
    const channel = mode === "deactivate"
      ? "alliance_partner:members:deactivate"
      : "alliance_partner:members:reactivate";
    const r = await window.zignagenixAPI.invoke(channel, {
      partnerUserId: user.id, reason,
    });
    /*
     * ⚠ THE SERVER'S OWN MESSAGE, not a generic shrug — the convention #700
     * set for the unlink control. REASON_REQUIRED, ORG_NOT_ACTIVE and
     * PARTNER_USER_NOT_FOUND each need a different action from the person
     * reading them, and "could not save" hides all three behind one word.
     */
    if (r && r.error) throw new Error(r.message || r.code);
    return r;
  }

  async function setStatus(newStatus) {
    if (!canAdmin || busy) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("alliance_partner:orgs:update", {
        id: orgId, status: newStatus,
      });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk(`Status changed to ${newStatus}.`);
      await load();
      onChanged();
    } catch (e) { flashErr(e.message); }
    finally { setBusy(false); }
  }


  // Credential-lifecycle ruling clause 1: the onboarder finishes the flow at the
  // table. The temporary password comes back exactly once and is shown until the
  // panel is dismissed — there is no way to retrieve it again, by design.
  async function issueCredential() {
    if (!canIssueCredential || busy) return;
    const email = (issueEmail || "").trim();
    const mobile = (issueMobile || "").trim();
    if (!email) { flashErr("Enter the partner's email address."); return; }
    /**
     * ⚠ Checked here too, not only on the server. The server is the authority
     * and refuses before anything is minted — but a staff member at the table
     * with the partner should learn the number is wrong NOW, while they can ask
     * for it again, rather than after a round trip.
     *
     * ⛔⛔ KEPT EXACTLY AS IT WAS — Card SALES-REP-EASE. <MobileField> now does
     * the country-to-E.164 conversion and refuses an invalid number inline, so
     * in practice this branch is unreachable from the UI. It stays because the
     * value's SHAPE is the contract with database/partnerPhone.js, and a guard
     * that is only ever satisfied is exactly the guard that catches the day a
     * future edit hands this function something else.
     */
    if (!mobile) { flashErr("Choose the country and enter the partner's mobile number — it is their sign-in code backup."); return; }
    if (!/^\+[1-9]\d{7,14}$/.test(mobile.replace(/\s/g, ""))) {
      flashErr("That mobile number could not be converted. Check the country and the digits.");
      return;
    }
    if (!window.confirm(
      `Create a PORTAL sign-in for "${email}"?\n\n` +
      `This creates their account on partners.zignagenix.net. A one-time ` +
      `temporary password will be shown ONCE — read it to them, do not send it. ` +
      `They must change it at their first sign-in.`
    )) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("alliance_partner:members:issue-credential", {
        organizationId: orgId, email, mobile, role: "admin",
        // ⚠ Set when repairing a partner the legacy button created: LINK the
        // new identity to their existing row instead of inserting a second one.
        linkExistingUserId: linkUserId,
      });
      if (r && r.error) {
        // The BAA refusal carries a human message; everything else falls back to
        // its code so a staff member always sees something actionable.
        throw new Error(r.message || r.code);
      }
      // ⚠ `temporaryPassword` — the field the shared provisioning core returns
      // on the on-screen delivery path. The legacy handler returned `password`.
      showCredential({ kind: 'issued',  email: r.email, password: r.temporaryPassword });
      setIssueEmail("");
      setIssueMobile("");
      setMobileFieldKey((n) => n + 1);
      setLinkUserId(null);
      await load();
    } catch (e) { flashErr(e.message); }
    finally { setBusy(false); }
  }

  async function accrualAction(action, accrualId) {
    if (!canAdmin || busy) return;
    let reason = null;
    if (action === "reject" || action === "clawback") {
      reason = window.prompt(`Reason for ${action}?`);
      if (!reason) return;
    }
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke(`alliance_partner:accruals:${action}`, {
        id: accrualId, reason,
      });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk(`Accrual ${action}d.`);
      await load();
    } catch (e) { flashErr(e.message); }
    finally { setBusy(false); }
  }

  /**
   * ⛔⛔ THE LOADING BRANCH IS FOR THE FIRST LOAD ONLY — Card
   * PORTAL-RESET-READOUT-c.
   *
   * `if (loading)` unmounted this ENTIRE subtree on every REFETCH, not just on
   * the first one. The member rows went with it, and so did
   * PortalSignInReset's one-time password — a value that exists nowhere else,
   * cannot be re-fetched, and had already been accepted by Cognito. Three
   * seconds of visibility, then gone.
   *
   * ⇒ `loading && !data` keeps the spinner for the genuine first load and lets
   * a REFRESH happen UNDERNEATH the rendered detail. Stale rows for 160 ms are
   * strictly better than destroying a credential.
   *
   * ⚠ This is the half that matters even after flashOk/flashErr were
   * stabilised. `onChanged` and the tab actions legitimately refetch, and any
   * one of them would have done the same damage. Fixing only the spurious
   * refetch would have left the trap armed for the next caller.
   */
  if (loading && !data) {
    return (
      <div style={{ gridColumn: "1/-1", padding: 24, background: "#F0F4F8", textAlign: "center", color: "#6C757D" }}>
        <Loader2 size={16} className="spin" style={{ verticalAlign: "middle" }} /> Loading partner detail…
      </div>
    );
  }
  if (!data) return null;
  const org = data.org;
  /*
   * ⚠ FAILS OPEN, DELIBERATELY. `provisioning` is new in this card. If it is
   * absent — an older server behind a rolling deploy, or a payload shape that
   * changes again later — the button stays ENABLED and the server refuses as
   * it always did. The alternative fails CLOSED and turns a missing field into
   * a permanently dead control with no explanation, which is a worse version
   * of the bug this card exists to fix.
   */
  const readyToIssue = data.provisioning ? data.provisioning.canIssueLogin !== false : true;

  return (
    <div style={{ gridColumn: "1/-1", background: "#F0F4F8", padding: 16 }}>

      {/* Tab strip */}
      <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
        {[
          { id: "overview", label: "Overview", icon: Handshake },
          { id: "tier",     label: "Tier History", icon: Award },
          { id: "accruals", label: "Accruals", icon: DollarSign },
          { id: "audit",    label: "Audit", icon: History },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                padding: "6px 12px", border: "none", borderRadius: 8,
                background: tab === t.id ? "#2E8B98" : "white",
                color: tab === t.id ? "white" : "#212529",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 5,
              }}>
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
        <button onClick={onClose} style={{
          marginLeft: "auto", background: "none", border: "none", color: "#6C757D",
          cursor: "pointer", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4,
        }}>
          <X size={12} /> Close
        </button>
      </div>

      <div style={{ background: "white", borderRadius: 10, padding: 16 }}>

        {tab === "overview" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
            <Field label="Legal name" value={org.legal_name} />
            <Field label="Contact email" value={org.contact_email} />
            <Field label="Contact phone" value={org.contact_phone} />
            <AgreementField
              label="BAA signed"
              value={org.baa_signed_at}
              canEdit={canAdmin}
              busy={busy}
              onRecord={(v) => recordAgreement("baa", v)}
              hint="Required before a portal login can be issued."
            />
            <AgreementField
              label="Contract signed"
              value={org.contract_signed_at}
              canEdit={canAdmin}
              busy={busy}
              onRecord={(v) => recordAgreement("contract", v)}
            />
            <Field label="Created" value={new Date(org.created_at).toLocaleString()} />

            {/*
              ⛔⛔ THIS IS WHERE THE TIER DROPDOWN WAS — Card
              PARTNER-MANUAL-COMMISSION. Ryland asked for "a commission
              percentage I manually input" in place of the tier section, and this
              is it.

              ⚠ RENDERED FOR EVERY READER, not just an editor: the panel shows
              the current rate to anyone who can see the partner and offers the
              input only to someone holding the permission. Someone who cannot
              change the number still needs to know what it is.

              ⚠ The tier dropdown is GONE, but the tier is not: the Tier History
              tab still renders, current_tier is untouched, and ledger rows keep
              tier_at_earn. It is history now, not a control — and nothing on
              this page can set it, which is the point.
            */}
            <CommissionRatePanel
              org={org}
              history={data.commission_rate_history || []}
              canEdit={canSetCommissionRate}
              busy={busy}
              onSave={setCommissionRate}
            />

            {canAdmin && (
              <>
                <div>
                  <label style={fieldLabel()}>Status</label>
                  {/*
                    ⛔⛔ TERMINATION IS INTERCEPTED — Card P124.
                    Since #702 it is not only a status: it deactivates every
                    member and ends every live session, in the same
                    transaction. A <select> that applies that on change, with
                    a caption mentioning only commissions, understates what
                    the click does. Any other status still saves immediately.
                  */}
                  <select
                    value={org.status}
                    data-testid="org-status-select"
                    onChange={e => {
                      if (e.target.value === "terminated") setPendingTermination(true);
                      else setStatus(e.target.value);
                    }}
                    style={fieldSelect()}
                  >
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="terminated">Terminated</option>
                  </select>
                  <div style={{ fontSize: 10, color: "#ADB5BD", marginTop: 3 }}>
                    {/* ⚠ CORRECTED BY P124. This said only "stop accruing new
                        commissions", which has been incomplete since #702. */}
                    Paused or terminated orgs stop accruing new commissions; existing accruals
                    remain. Terminating also deactivates every member and ends their sessions.
                  </div>
                </div>
              </>
            )}

            {org.notes && (
              <div style={{ gridColumn: "1/-1" }}>
                <label style={fieldLabel()}>Notes</label>
                <div style={{ fontSize: 12, color: "#212529", whiteSpace: "pre-wrap" }}>{org.notes}</div>
              </div>
            )}

            {/*
              Documents — Card PARTNER-DOCUMENTS (Ryland via Daniel, 8 Oct 2026).
              Placed ABOVE Members on purpose: the paperwork is what a staff
              member reaches for when they open a partner record, and the BAA
              date just above it is the gate on issuing a login, so the two
              facts that block onboarding now sit together.
              ⛔⛔ RENDERED ONLY FOR alliance_partner:admin, AND THE GUARD IS ON
              THE SECTION, NOT JUST ITS BUTTONS. The component calls
              documents:list on mount, so rendering it for everyone meant a
              patient_experience user (who holds :read, not :admin) got an
              ACCESS_DENIED toast on EVERY partner they opened — a permission
              check working correctly, surfaced as a fault. Mounting nothing
              means calling nothing.
              ⚠ This is still a courtesy, not the control: all five channels
              are gated server-side on the same key.
              ⭐ refreshNonce WIRED 2026-10-09, once #608 merged (835106bc).
              The page's Refresh button increments it and every child watching
              it re-reads. Without this the documents list would be the ONE
              thing on an open record that Refresh did not reload — precisely
              the defect #608 exists to fix, reintroduced one section lower.
              The component has accepted the prop since it was written
              (defaulted to 0), so this is the one-line change the previous
              note promised.
            */}
            {canAdmin && (
              <PartnerDocuments
                orgId={orgId}
                canManage={canAdmin}
                flashOk={flashOk}
                flashErr={flashErr}
                refreshNonce={refreshNonce}
              />
            )}

            {/* Members. The only credential control here is the PORTAL sign-in reset;
                the legacy password reset and partner "Reset 2FA" are retired (3 Oct 2026). */}
            <div style={{ gridColumn: "1/-1" }}>
              <label style={fieldLabel()}>Members</label>
              {(data.users || []).length === 0 && (
                <div style={{ color: "#ADB5BD", fontSize: 12 }}>
                  No members.{canIssueCredential ? " Issue a login below to let this partner sign in." : ""}
                </div>
              )}
              {(data.users || []).map(u => (
                <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", padding: "6px 0", borderBottom: "1px solid #F1F3F5" }}>
                  <div style={{ fontSize: 12, color: "#212529" }}>
                    {u.email} <span style={{ color: "#ADB5BD" }}>· {u.role}{u.totp_enabled ? " · 2FA on" : " · 2FA off"}</span>
                    {/*
                      ⛔⛔ MASKED, AND MASKED BY CONSTRUCTION — Card PARTNER-PHONE-SMS.
                      This is NOT a full number with a mask applied at render. The
                      CRM only ever holds `phone_last4` (migration 181); the full
                      E.164 lives in Cognito. So there is no code path here that
                      COULD print a dialable number, which is a stronger guarantee
                      than remembering to mask one.

                      ⚠ "no mobile" is a fact staff need, not a blank. Every
                      partner user provisioned before this card has none — AdminCreateUser
                      sent only email and email_verified — and those partners
                      cannot receive an SMS fallback code at all. An em dash says
                      so; an empty span would read as a rendering fault.
                    */}
                    <span style={{ color: "#ADB5BD" }}>
                      {" · "}
                      {u.phone_last4
                        ? `•••• ${u.phone_last4}`
                        : <em style={{ color: "#868E96" }}>no mobile</em>}
                    </span>
                    {/*
                      ⛔⛔ WHETHER THIS PERSON CAN SIGN IN — Card P124.
                      `is_active` has been in this payload all along and was
                      never rendered, so the page showed a deactivated member
                      exactly like a working one.
                    */}
                    {u.is_active === false && (
                      <span data-testid={`member-deactivated-${u.id}`} style={{
                        marginLeft: 6, padding: "1px 6px", borderRadius: 999,
                        background: "#FEE2E2", color: "#991B1B", fontSize: 10, fontWeight: 700,
                      }}>Deactivated</span>
                    )}
                    <MemberStatusTrail userId={u.id} events={data.member_status_events} />
                  </div>
                  {/* ⛔ "Reset 2FA" REMOVED — Daniel's ruling, 3 Oct 2026. It cleared
                      alliance_partner_users.totp_secret_encrypted, which the
                      Cognito-backed portal never reads: for a partner who lost
                      their phone it was a no-op that reported success, sitting
                      right beside the control that does work. The channel still
                      exists and now REFUSES (LEGACY_2FA_RESET_RETIRED); there is
                      deliberately no button for it. A lost partner authenticator
                      is an escalation — runbook §3. */}
                  {/* ⛔ THE PORTAL ONE. Card PARTNER-RESET-STAFF — this is the
                      reset that actually affects partners.zignagenix.net. It is
                      labelled PORTAL because the legacy "Reset password"
                      elsewhere is a no-op for the real portal.
                      ⚠ A DIRECT CHILD OF THE ROW, not of the button group. In
                      the group its open panel resolved its flex basis against
                      the group rather than the row, so it squeezed the member's
                      email to a sliver — and inconsistently, depending on the
                      panel's height. Rendered here it is either a button at the
                      end of the row or a full-width panel beneath it, with no
                      third possibility. Caught by looking at the screenshots.
                      ⚠ The temporary password lives inside this component and
                      is never handed to the page's flash banner. */}
                  {/*
                    ⛔⛔ THE REPAIR PATH — Card ISSUE-LOGIN-REBUILD. A member with
                    NO external_id was created by the legacy button, which wrote
                    Postgres only: they CANNOT sign in to partners.zignagenix.net
                    and "Forgot password?" sends them nothing, because
                    PreventUserExistenceErrors masks UserNotFound. Nothing on
                    this page used to say so.

                    ⚠ It LINKS to this row rather than creating a second member:
                    their referral history is attached to this id, and migration
                    144's write-once attribution trigger makes a split unfixable.
                  */}
                  {canIssueCredential && !u.external_id ? (
                    <button
                      type="button"
                      onClick={() => { setLinkUserId(u.id); setIssueEmail(u.email || ""); }}
                      title="This member has no portal account yet, so they cannot sign in."
                      style={{ padding: "5px 10px", fontSize: 11, fontWeight: 700, background: "#fff", color: "#A16207", border: "1px solid #FDE68A", borderRadius: 5, cursor: "pointer" }}>
                      {/* ⚠ "Set up", not "Create" — the SUBMIT button in link
                          mode says "Create portal sign-in", and two controls
                          with the same name on one screen is ambiguous for a
                          staff member mid-task. Caught by a test that could not
                          tell them apart either. */}
                      Set up portal sign-in
                    </button>
                  ) : null}
                  {/*
                    ⛔⛔ EDIT MOBILE — Card PARTNER-PHONE-EDIT, 3 Oct 2026.
                    Beside the reset because it is the same staff member on the
                    same phone call: "I can't get in" and "my number changed"
                    arrive together, and the partner's handset is what the
                    recovery code would go to.

                    ⚠ SAME PERMISSION as the reset ({sysadmin, admin}) — changing
                    the handset that receives sign-in codes IS a credential
                    capability, not contact-detail housekeeping.

                    ⚠ It cannot pre-fill: the CRM holds four digits and the task
                    role has no AdminGetUser, so there is no number here to put
                    in the field. The masked current value is shown instead.
                  */}
                  <EditMobile
                    member={u}
                    canEdit={canResetPortalSignIn}
                    onSaved={({ phoneLast4 }) => {
                      // ⚠ RELOAD, so the masked value on the row agrees with
                      // what was just saved. A flash alone would leave the row
                      // showing the OLD last four beside a success message —
                      // and this screen's whole job is to say which handset a
                      // code reaches.
                      load();
                      flashOk(`Mobile for ${u.email} set to ending ${phoneLast4}. `
                        + `Not verified — text messages are not switched on yet.`);
                    }}
                  />
                  <PortalSignInReset
                    member={u}
                    canReset={canResetPortalSignIn}
                    /*
                      ⛔ THE PASSWORD GOES UP, NOT INTO THIS ROW. The row is
                      destroyed by any refetch; the page is not.
                    */
                    onCredential={(c) => showCredential({ ...c, kind: 'reset' })}
                    onResetComplete={() => flashOk(`Portal sign-in reset for ${u.email} — the temporary password is shown at the top of this page, once.`)}
                  />
                  {/*
                    ⛔⛔ RESET AUTHENTICATOR — Card RESET-AUTHENTICATOR, 5 Oct 2026.
                    Beside the sign-in reset because "I lost my phone" and "I
                    forgot my password" arrive on the SAME call, and until now
                    only one of them had a button. The pool is MFA-required with
                    TOTP only, so a lost handset was a hard lockout with no staff
                    action behind it (runbook §3 said "escalate to Daniel").

                    ⚠ SAME PERMISSION as the reset ({sysadmin, admin}) — and this
                    one transfers MORE control, not less: after it, whoever signs
                    in next binds their own device as the second factor. The
                    confirm step names the partner and states that precondition.

                    ⛔ NOTHING SECRET COMES BACK. The QR code is minted by Cognito
                    for the PARTNER inside the portal at their next sign-in. A
                    staff-visible enrolment secret would let the person who ran
                    the reset enrol their own device.
                  */}
                  <AuthenticatorReset
                    member={u}
                    canReset={canResetPortalSignIn}
                    onResetComplete={() => flashOk(`Authenticator reset for ${u.email} — ask them to sign in; they'll be shown a new QR code.`)}
                  />
                  {/*
                    ⛔⛔ THE CONTROL #702 LEFT WITHOUT A BUTTON — Card P124.
                    Last in the row, after the recovery controls, because it is
                    the destructive one: the others get someone back IN.

                    ⚠ GATED ON alliance_partner:admin, which is the permission
                    the handler itself demands. #700's lesson, applied: a button
                    offered on a looser permission than the server enforces is a
                    button that exists to fail.
                  */}
                  {canAdmin && (
                    <button
                      type="button"
                      data-testid={`member-toggle-${u.id}`}
                      onClick={() => setMemberAction({
                        user: u, mode: u.is_active === false ? "reactivate" : "deactivate",
                      })}
                      disabled={busy}
                      title={u.is_active === false
                        ? "Restore this member's ZignaGenix access and portal sign-in"
                        : "End this member's sessions and disable their portal sign-in"}
                      style={{
                        padding: "5px 10px", fontSize: 11, fontWeight: 700, borderRadius: 5,
                        cursor: busy ? "default" : "pointer", background: "#fff",
                        color: u.is_active === false ? "#15803D" : "#991B1B",
                        border: `1px solid ${u.is_active === false ? "#BBF7D0" : "#FCA5A5"}`,
                      }}>
                      {u.is_active === false ? "Reactivate" : "Deactivate"}
                    </button>
                  )}
                </div>
              ))}

              {/* Issue a login (credential-lifecycle ruling, 2026-08-28).
                  A visibly labelled control, not an icon — this is the step that
                  was missing entirely, so it must be findable without hunting. */}
              {canIssueCredential && (
                <div style={{ marginTop: 10, padding: 10, background: "#F8F9FA", border: "1px solid #E9ECEF", borderRadius: 6 }}>
                  <label style={fieldLabel()}>
                    {linkUserId ? "Create a PORTAL sign-in for this member" : "Issue a PORTAL login"}
                  </label>
                  {/*
                    ⛔⛔ SAY WHY IT WILL BE REFUSED, BEFORE IT IS PRESSED.
                    The gate stays exactly as it is (Daniel's 2026-09-28 ruling:
                    there is no bypass). What changes is that its two conditions
                    are now visible: the button was enabled, the refusal was a
                    toast that cleared itself in six seconds, and the staff
                    member was left believing the user had been created and lost.
                  */}
                  <IssueReadiness provisioning={data.provisioning} />
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input
                      type="email"
                      value={issueEmail}
                      disabled={busy}
                      /* ⛔⛔ READ-ONLY IN LINK MODE. The address identifies WHICH
                         human gets a sign-in; the member row identifies WHOSE
                         referrals it carries. Letting staff edit one and not the
                         other is how those two become different people — the
                         server refuses it now, but the field should never have
                         invited it. readOnly, not disabled: a disabled input is
                         skipped by screen readers and greys out of the tab
                         order, and staff still need to READ the address they
                         are about to use. */
                      readOnly={!!linkUserId}
                      onChange={(e) => setIssueEmail(e.target.value)}
                      placeholder="partner@theircompany.com"
                      aria-label="Partner email address for the new login"
                      title={linkUserId ? "Linking to an existing member — their address cannot be changed here." : undefined}
                      style={{ flex: "1 1 240px", minWidth: 0, padding: "6px 8px", fontSize: 12, border: "1px solid #CED4DA", borderRadius: 5, background: linkUserId ? "#E9ECEF" : "#fff", color: linkUserId ? "#495057" : undefined }}
                    />
                    {/* ⚠ REQUIRED. The mobile is the partner's sign-in code
                        backup (Card PARTNER-PHONE-SMS), so it is collected at
                        the table while they are sitting there — not chased
                        afterwards.
                        ⛔⛔ Card SALES-REP-EASE: the rep picks the COUNTRY and
                        types the number the way the partner reads it out. The
                        browser converts to E.164 and shows what will be saved.
                        We no longer guess a country — we ask for it. */}
                    <MobileField
                      key={mobileFieldKey}
                      idPrefix="issue-mobile"
                      numberLabel="Partner mobile number"
                      disabled={busy}
                      onChange={setIssueMobile}
                    />
                    <button
                      disabled={busy || !issueEmail.trim() || !issueMobile.trim() || !readyToIssue}
                      title={readyToIssue ? undefined : "This partner cannot be issued a login yet — see the note above."}
                      onClick={issueCredential}
                      style={{ padding: "7px 14px", fontSize: 12, fontWeight: 700, background: "#2E8B98", color: "#fff", border: "1px solid #2E8B98", borderRadius: 5, cursor: busy ? "default" : "pointer", opacity: busy || !issueEmail.trim() || !issueMobile.trim() || !readyToIssue ? 0.5 : 1 }}>
                      {linkUserId ? "Create portal sign-in" : "Issue login"}
                    </button>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, color: "#868E96" }}>
                    {linkUserId
                      ? "Links a portal sign-in to the member you selected — their existing referrals stay with them."
                      : "Creates their account on partners.zignagenix.net."}
                    {" "}A one-time temporary password is shown once; read it to
                    them, do not send it. They must change it at first sign-in.
                    {linkUserId ? (
                      <> {" "}
                        {/* ⚠ CLEARS THE ADDRESS TOO. Leaving the linked
                            member's address behind in a now-editable field is
                            the same mix-up one step later: the next click would
                            CREATE a new member carrying somebody else's email. */}
                        <button type="button" onClick={() => { setLinkUserId(null); setIssueEmail(""); }}
                          style={{ background: "none", border: "none", padding: 0, color: "#2E8B98", textDecoration: "underline", cursor: "pointer", fontSize: 11 }}>
                          Cancel linking
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              )}

              {/* The password, shown ONCE. Deliberately loud, and deliberately
                  not dismissible by accident — once this panel is gone the value
                  cannot be retrieved, only reset. */}
              {/*
                ⛔⛔ THE ISSUED PASSWORD MOVED TO THE PAGE — Card
                PORTAL-RESET-READOUT-d. It lived in OrgDetail state and had the
                IDENTICAL defect as the reset readout: any refetch of this
                detail unmounted the subtree and destroyed a credential that
                cannot be re-issued without burning another Cognito reset. It is
                now handed to the page and rendered above the list.
              */}
            </div>
          </div>
        )}

        {tab === "tier" && (
          <div>
            <div style={sectionHeader()}>Tier history</div>
            {(data.tier_history || []).length === 0 && (
              <div style={{ color: "#ADB5BD", fontSize: 12, padding: 8 }}>No tier changes recorded.</div>
            )}
            {(data.tier_history || []).map(h => (
              <div key={h.id} style={{
                padding: 8, borderBottom: "1px solid #F1F3F5",
                display: "grid", gridTemplateColumns: "100px 120px 100px 1fr", gap: 8, alignItems: "center",
                fontSize: 12,
              }}>
                <span style={{ color: "#6C757D" }}>{new Date(h.effective_from).toLocaleDateString()}</span>
                <span style={{
                  background: TIER_COLOR[h.tier], color: "white", padding: "1px 8px",
                  borderRadius: 999, fontWeight: 700, fontSize: 10, width: "fit-content",
                }}>{TIER_LABEL[h.tier]}</span>
                <span style={{ fontSize: 10, color: "#6C757D" }}>{h.set_by}</span>
                <span style={{ fontSize: 11, color: "#212529" }}>
                  {h.completed_therapies_count != null && (
                    <span><strong>{h.completed_therapies_count}</strong> therapies/12mo. </span>
                  )}
                  {h.notes && <em>{h.notes}</em>}
                </span>
              </div>
            ))}
          </div>
        )}

        {tab === "accruals" && (
          <div>
            <div style={sectionHeader()}>Commission accruals (most recent 100)</div>
            {accruals.length === 0 && (
              <div style={{ color: "#ADB5BD", fontSize: 12, padding: 8 }}>No accruals yet.</div>
            )}
            {accruals.map(a => (
              <div key={a.id} style={{
                padding: 10, borderBottom: "1px solid #F1F3F5",
                display: "grid", gridTemplateColumns: "100px 100px 120px 100px 1fr auto", gap: 8, alignItems: "center",
                fontSize: 12,
              }}>
                <span style={{ color: "#6C757D" }}>{new Date(a.accrued_at).toLocaleDateString()}</span>
                <span>
                  <strong>${(a.commission_amount_cents / 100).toFixed(2)}</strong>
                  <span style={{ color: "#ADB5BD", marginLeft: 4, fontSize: 10 }}>
                    @ {(a.commission_rate_bps / 100).toFixed(1)}%
                  </span>
                </span>
                <span style={{
                  background: ACCRUAL_STATUS_COLOR[a.status],
                  color: "white", padding: "1px 8px", borderRadius: 999,
                  fontSize: 10, fontWeight: 700, width: "fit-content",
                }}>{a.status}</span>
                <span style={{ fontSize: 10, color: "#6C757D" }}>{a.tier_at_accrual || "—"}</span>
                <span style={{ fontSize: 10, color: "#ADB5BD" }}>
                  Invoice {a.billing_invoice_id} · {a.accrual_type}
                </span>
                {canAdmin && a.status === "accrued" && (
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => accrualAction("approve", a.id)} style={smallBtn("#62BB46")}
                      title="Approve before hold expires">Approve</button>
                    <button onClick={() => accrualAction("reject", a.id)} style={smallBtn("#DC3545")}>Reject</button>
                  </div>
                )}
                {canAdmin && (a.status === "approved" || a.status === "paid_out") && (
                  <button onClick={() => accrualAction("clawback", a.id)} style={smallBtn("#DC3545")}>Clawback</button>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "audit" && (
          <div>
            <div style={sectionHeader()}>Audit log (most recent 50)</div>
            {audit.length === 0 && (
              <div style={{ color: "#ADB5BD", fontSize: 12, padding: 8 }}>No audit entries.</div>
            )}
            {audit.map(a => (
              <div key={a.id} style={{
                padding: 8, borderBottom: "1px solid #F1F3F5", fontSize: 11,
                display: "grid", gridTemplateColumns: "140px 200px 1fr", gap: 10, alignItems: "center",
              }}>
                <span style={{ color: "#6C757D" }}>{new Date(a.created_at).toLocaleString()}</span>
                <code style={{ color: "#2E8B98", fontSize: 10 }}>{a.action}</code>
                <span style={{ color: "#212529" }}>{a.summary || "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Card P124 — both confirmations. Rendered at the OrgDetail root so a
          dialog is never clipped by a scrolling panel. */}
      {memberAction && (
        <MemberActionConfirm
          user={memberAction.user}
          mode={memberAction.mode}
          run={runMemberAction}
          onCancel={() => setMemberAction(null)}
          onDone={async (result) => {
            const { user, mode } = memberAction;
            setMemberAction(null);
            /*
             * ⛔⛔ NEVER REPORT SUCCESS FOR THE HALF THAT FAILED — Card P127.
             *
             * members:reactivate restores the CRM row and then asks Cognito to
             * re-enable the portal identity. The second half can fail on its
             * own (AccessDeniedException until Daniel applies the IAM grant,
             * or any provider error), and the handler reports that rather than
             * throwing, because the first half DID land and rolling it back
             * would discard a successful, audited change.
             *
             * ⚠ So the flash is built from the server's answer. A bare
             * "Reactivated" over a member who still cannot sign in to
             * partners.zignagenix.net is exactly the confident-wrong readout
             * this page has been removing all week. `absent` is NOT a failure:
             * it means there is no portal identity to enable.
             */
            const c = (result && result.cognito) || {};
            if (mode !== "reactivate") {
              flashOk(`Deactivated ${user.email}.`);
            } else if (c.absent) {
              // ⚠ No portal identity to restore. Not a failure — a partner row
              // that predates Cognito, or one never issued a credential.
              flashOk(`Reactivated ${user.email}. They have no portal sign-in to restore.`);
            } else if (c.reEnabled === true) {
              flashOk(`Reactivated ${user.email}.`);
            } else {
              /*
               * ⛔⛔ FAIL-CLOSED, AND THIS WAS A REAL DEFECT IN MY FIRST DRAFT.
               * That version enumerated the failures it knew about — `error`,
               * then `POOL_NOT_CONFIGURED` — and flashed plain success for
               * anything else. An ALLOWLIST OF FAILURES fails open: a server
               * still running the P124 handler returns
               * `{reEnabled:false, reason:"ADMIN_ENABLE_USER_NOT_GRANTED"}`
               * with no `error` key at all, and the page called that
               * "Reactivated". The page and the server are not guaranteed to
               * deploy together, so that is a shape this code will really meet.
               *
               * ⇒ The only two ways to claim the portal half are `absent` and
               * an explicit `reEnabled === true`. Everything else says which
               * half is missing and why, in whatever terms the server gave.
               */
              const why = c.error
                || (c.reason === "POOL_NOT_CONFIGURED"
                      ? "the partner user pool is not configured on this server"
                      : c.reason)
                || "the identity provider did not confirm it";
              flashErr(
                `ZignaGenix access restored for ${user.email}; portal sign-in NOT re-enabled: `
                + `${why}. They cannot sign in to partners.zignagenix.net until this is finished.`);
            }
            await load();
            onChanged();
          }}
        />
      )}
      {pendingTermination && (
        <TerminateOrgConfirm
          org={org}
          users={data.users || []}
          busy={busy}
          onCancel={() => setPendingTermination(false)}
          onConfirm={async () => { setPendingTermination(false); await setStatus("terminated"); }}
        />
      )}
    </div>
  );
}

// ─── Create Org modal ──────────────────────────────────────────────────────
function CreateOrgModal({ onClose, onCreated, flashErr }) {
  const [name, setName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("alliance_partner:orgs:create", {
        name: name.trim(),
        legalName: legalName.trim() || null,
        contactEmail: contactEmail.trim() || null,
        contactPhone: contactPhone.trim() || null,
        notes: notes.trim() || null,
      });
      if (r && r.error) throw new Error(r.message || r.code);
      onCreated();
    } catch (e) { flashErr(e.message); setBusy(false); }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: "white", borderRadius: 12, padding: 24, maxWidth: 480, width: "90%",
      }} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: "0 0 16px", fontSize: 18, color: "#212529" }}>New partner organization</h2>
        <label style={fieldLabel()}>Org name *</label>
        <input type="text" value={name} onChange={e => setName(e.target.value)} style={fieldInput()} autoFocus />
        <label style={fieldLabel()}>Legal name (for 1099 + commission statements)</label>
        <input type="text" value={legalName} onChange={e => setLegalName(e.target.value)} style={fieldInput()} />
        <label style={fieldLabel()}>Primary contact email</label>
        <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} style={fieldInput()} />
        <label style={fieldLabel()}>Primary contact phone</label>
        <input type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)} style={fieldInput()} />
        <label style={fieldLabel()}>Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} style={fieldTextarea()} rows={3}
          placeholder="Territory considerations, special terms, contract URL, etc." />
        <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={btnGhost()}>Cancel</button>
          <button onClick={save} disabled={busy || !name.trim()} style={name.trim() ? btnPrimary() : btnDisabled()}>
            <Plus size={12} /> Create
          </button>
        </div>
        <div style={{ marginTop: 8, fontSize: 10, color: "#ADB5BD" }}>
          Org starts at <strong>Silver</strong> tier (35% commission). Tier auto-promotes nightly based on completed therapies.
        </div>
      </div>
    </div>
  );
}

// ─── Tiny helpers ─────────────────────────────────────────────────────────
function Kpi({ label, value, color, icon }) {
  return (
    <div style={{
      background: "white", borderRadius: 10, padding: 12,
      border: "1px solid #E9ECEF", display: "flex", alignItems: "center", gap: 10,
    }}>
      <div style={{
        background: `${color}18`, color, padding: 6, borderRadius: 7,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>{icon}</div>
      <div>
        <div style={{ fontSize: 10, color: "#6C757D", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#212529" }}>{value}</div>
      </div>
    </div>
  );
}

/**
 * Says, before the button is pressed, whether a portal login can actually be
 * issued for this partner — and if not, which of the two gates is holding it.
 *
 * ⛔ This component NEVER asserts "ready" from missing information. An absent
 * `provisioning` block renders nothing at all (the caller leaves the button
 * enabled and the server stays the authority); an `unknown` consumer state
 * renders the SAME blocked treatment as a dead one, because a gate that cannot
 * see whether the consumer is alive has not established that it is — the exact
 * words database/partnerAccountProvisioning.js uses about itself.
 */
function IssueReadiness({ provisioning }) {
  if (!provisioning) return null;

  const box = (bg, border, color, title, body) => (
    <div style={{ background: bg, border: `1px solid ${border}`, color, borderRadius: 5, padding: "7px 9px", fontSize: 11, marginBottom: 8 }}>
      <strong>{title}</strong>{body ? <> — {body}</> : null}
    </div>
  );

  if (!provisioning.agreementRecorded) {
    return box("#FEF3C7", "#FDE68A", "#92400E",
      "Partner agreement not recorded",
      <>record the <strong>BAA signed</strong> date above and this partner can be issued a login. Nothing is created until then.</>);
  }

  const state = provisioning.consumer && provisioning.consumer.state;
  if (!provisioning.consumer || !provisioning.consumer.live) {
    /*
     * ⚠ "usually under 5 minutes" is not a guess: the referral reader runs on
     * a 5-minute schedule and the liveness bound is 15 minutes (three missed
     * passes) — database/partnerConsumerLiveness.js. A never-ran state is NOT
     * a wait, so it says something different rather than promising a clock
     * that may never tick.
     */
    const detail = state === "never_ran"
      ? "the referral reader has never run, so this needs an administrator — a login now would produce referrals nobody receives."
      : state === "unknown"
        ? "we cannot confirm the referral reader is running, so a login is not being offered. Try Refresh; if it persists, tell an administrator."
        : "waiting for the referral reader — usually under 5 minutes. Press Refresh to check again.";
    return box("#FEF3C7", "#FDE68A", "#92400E", "Login not available yet", detail);
  }

  return box("#DCFCE7", "#BBF7D0", "#166534", "Login ready",
    "the agreement is recorded and the referral reader is running.");
}

/**
 * A date the paperwork was signed, recordable in place by an admin.
 *
 * ⚠ Reads as a plain <Field> for anyone without alliance_partner:admin, so the
 * page does not grow an affordance that 403s on click.
 *
 * ⛔ `type="date"` and nothing else. The server parses to a YYYY-MM-DD string
 * and refuses INVALID_DATE, so a free-text box would only move the refusal
 * later; the native picker also means no locale ambiguity between a staff
 * member in San Diego and one in Cancun about what 03/04 means.
 */
function AgreementField({ label, value, canEdit, busy, onRecord, hint }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const shown = value ? new Date(value).toLocaleDateString() : null;

  if (!canEdit) return <Field label={label} value={shown} />;

  return (
    <div>
      <div style={fieldLabel()}>{label}</div>
      {editing ? (
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="date"
            value={draft}
            autoFocus
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            style={{ padding: "5px 7px", fontSize: 12, border: "1px solid #CED4DA", borderRadius: 5 }}
          />
          <button
            type="button"
            disabled={busy || !draft}
            onClick={async () => { await onRecord(draft); setEditing(false); }}
            style={{ ...btnPrimary(), padding: "5px 10px", fontSize: 11, opacity: busy || !draft ? 0.5 : 1 }}
          >
            Save
          </button>
          <button type="button" disabled={busy} onClick={() => setEditing(false)} style={{ ...btnGhost(), padding: "5px 10px", fontSize: 11 }}>
            Cancel
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: shown ? "#212529" : "#ADB5BD" }}>{shown || "— not recorded"}</span>
          <button
            type="button"
            disabled={busy}
            onClick={() => { setDraft(value ? new Date(value).toISOString().slice(0, 10) : ""); setEditing(true); }}
            style={{ background: "none", border: "none", padding: 0, color: "#2E8B98", textDecoration: "underline", cursor: "pointer", fontSize: 11 }}
          >
            {shown ? "Change" : "Record"}
          </button>
        </div>
      )}
      {!shown && hint && (
        <div style={{ fontSize: 10, color: "#B45309", marginTop: 3 }}>{hint}</div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={fieldLabel()}>{label}</div>
      <div style={{ fontSize: 13, color: value ? "#212529" : "#ADB5BD" }}>{value || "—"}</div>
    </div>
  );
}

function sectionHeader() {
  return {
    fontSize: 11, fontWeight: 700, color: "#6C757D",
    textTransform: "uppercase", letterSpacing: 0.5,
    marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid #F1F3F5",
  };
}
function fieldLabel() {
  return { display: "block", fontSize: 11, fontWeight: 600, color: "#6C757D", marginTop: 6, marginBottom: 3 };
}
function fieldInput() {
  return { width: "100%", padding: "6px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #CED4DA", boxSizing: "border-box" };
}
function fieldSelect() {
  return { ...fieldInput(), background: "white", cursor: "pointer", width: "auto" };
}
function fieldTextarea() {
  return { ...fieldInput(), fontFamily: "inherit", resize: "vertical" };
}
function btnPrimary() {
  return {
    background: "#2E8B98", color: "white", border: "none",
    padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
  };
}
function btnDisabled() {
  return { ...btnPrimary(), background: "#CED4DA", color: "#6C757D", cursor: "not-allowed" };
}
function btnGhost() {
  return {
    background: "white", border: "1px solid #DEE2E6", color: "#6C757D",
    padding: "5px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 4,
  };
}
function smallBtn(color) {
  return {
    background: color, color: "white", border: "none",
    padding: "3px 8px", borderRadius: 4, fontSize: 10, fontWeight: 600,
    cursor: "pointer",
  };
}
